import { createContext, useState, useContext, useEffect } from "react";
import { verifyAuth } from "../services/AuthService";

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication on app load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // console.log("UserContext: Checking authentication...");
        const { data } = await verifyAuth();

        // console.log("UserContext: Verify response:", data);

        if (data && data.role) {
          setUser({
            email: data.email,
            username: data.username,
            role: data.role,
            authenticated: data.authenticated,
          });
          setIsAuthenticated(true);
          // console.log("UserContext: User authenticated with role:", data.role);
        } else {
          // console.warn("UserContext: No role in response");
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        // console.log("UserContext: User not authenticated:", error.message);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Logout function
  const logout = () => {
    // console.log("UserContext: Logging out user");
    setUser(null);
    setIsAuthenticated(false);
  };

  // Update user function
  const updateUser = (userData) => {
    // console.log("UserContext: Updating user:", userData);
    setUser(userData);
  };

  // Function to manually refresh user data after login
  // Returns the user data directly so we don't have to wait for state update
  const refreshUser = async () => {
    try {
      // console.log("UserContext: Refreshing user data...");
      const { data } = await verifyAuth();

      if (data && data.role) {
        const userData = {
          email: data.email,
          username: data.username,
          role: data.role,
          authenticated: data.authenticated,
        };

        setUser(userData);
        setIsAuthenticated(true);
        // console.log("UserContext: User refreshed with role:", data.role);

        // Return the user data directly so caller can use it immediately
        return userData;
      }
      return null;
    } catch (error) {
      // console.error("UserContext: Error refreshing user:", error);
      return null;
    }
  };

  const value = {
    user,
    isLoading,
    isAuthenticated,
    logout,
    updateUser,
    refreshUser,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// Custom hook to use the context
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within UserProvider");
  }
  return context;
};
